/* eslint-disable unused-imports/no-unused-imports */
import React, {useEffect, useMemo, useRef, useState} from "react"
import {Bling as GPT, Events} from "react-gpt"
import {AdsConfig, AdvertsContent, ItemsPerRow} from "models/SearchState"
import {TextModel} from "../../models/Text"
import {LOAD_MORE, PRODUCT_LOAD_TRIGGER} from "../../models/PageLoadTrigger"
import InjectedAd from "../injectedAd"
import LoadingCover from "../loadingCover"
import SearchProviderApiCall from "../searchProviderApiCall"
import Product from "../product"
import connect from "./connect"
import BackToTop from "../backToTop"
import {GPT_ADVERT_ORIGIN, PRODUCT_TYPE, SUIT_TYPE} from "../../config/constants"
import {ProductItem} from "../../models/Product"
import {useInfiniteScroll} from "../../hooks/useInfiniteScroll"
import {useInitialScrollPosition} from "./useInitialScrollPosition"
import {useScrollPositionTracking} from "./useScrollPositionTracking"
import {useHideFooterUntilLastPage} from "../../hooks/useHideFooterUntilLastPage"
import publishProductNumbersToMonetate from "../../events/publishProductNumbersToMonetate"
import PublishProductSummaryTrackPage from "../../events/publishProductSummaryTrackPage"
import {handleBloomreachCategoryInfo} from "../../events/trackEvent/events/handleBloomreachCategoryInfo"
import {SeoLink} from "../SeoLink"
import {ConnectPrevNextSpinner} from "../spinner"
import {ProductGrid, ProductGridItem, NoResultsContainer, ProductGridWrapper} from "./components"
import TrackInjectedAdLoad from "../../events/trackEvent/events/trackInjectedAdLoad"
import TrackInjectedAdClick from "../../events/trackEvent/events/trackInjectedAdClick"
import ProductSummary from "../../compositions/product_summary"
import LoadMoreArea from "../loadMoreArea"
import {Ads} from "../Ads"
import {SaleOrClearance} from "../../models/Request"
import {useIsOnClient} from "../../hooks/useIsOnClient"

// WARNING: This id is used within ProdSum to scope a CS variable related to the tiles height
// If this id is changed, the CSS variable in ProdSum will need to be updated
export const productIdFor = (itemNumber: string, type = PRODUCT_TYPE) => {
    let tileId = itemNumber
    if (type === SUIT_TYPE && itemNumber.length > 6) {
        // slice suit type id to get item number e.g. c_jU15803_tU15804 => U15803
        tileId = itemNumber.slice(3, 9)
    }
    return `plp-product-summary-tile-${tileId}`
}

interface ProductsComponentProps {
    items: ProductItem[]
    endPage: number
    startPage: number
    itemsPerPage: number
    itemsPerRow: ItemsPerRow
    hasNextPage: boolean
    requestedPage: number
    hasPreviousPage: boolean
    isFetchingNextPage: boolean
    isFetchingPreviousPage: boolean
    isFetchingPageItems: boolean
    fetchPreviousPage: () => Promise<any>
    fetchNextPage: () => Promise<any>
    unfixFacets: VoidFunction
    fixFacets: VoidFunction
    siteUrl: string
    useDevEsi: boolean
    fetchTriggerOffset: number
    isAutocorrected: boolean
    hasSelectedFilters: boolean
    text: TextModel
    url: string
    searchCategoryId: string
    searchCategoryName: string
    pushSearchResultsEvent: () => void
    removeAdSpacerFromItems: () => void
    setInitialScrollCompleted: (boolean) => any
    initialScrollCompleted: boolean
    firstElementId: string
    setFirstElementId: (string) => any
    injectedAdHiddenForTimeout: boolean
    injectedAdsEnabled: boolean
    selectedSort: string
    filterSelectedCount: number
    hideFooterUntilLastPage: boolean
    productLoadTrigger: PRODUCT_LOAD_TRIGGER
    im: string
    productSummaryTemplateName: string | undefined
    imageRatio: string | undefined
    enableNewProductSummary: boolean
    isAdsEnabled: boolean
    urlIsSaleOrClearance: boolean
    saleOrClearance: SaleOrClearance
    advertsContent: AdvertsContent[]
    adsConfig: AdsConfig
}

export const Products = (props: ProductsComponentProps) => {
    const {isOnClient} = useIsOnClient()
    const adRenderedRef = useRef(false)
    const [shouldRemoveInjectedAdSpacer, setShouldRemoveInjectedAdSpacer] = useState<boolean>(false)
    const {
        items,
        endPage,
        startPage,
        itemsPerPage,
        itemsPerRow,
        hasNextPage,
        requestedPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isFetchingPageItems,
        fetchPreviousPage,
        fetchNextPage,
        unfixFacets,
        fixFacets,
        siteUrl,
        useDevEsi,
        fetchTriggerOffset,
        isAutocorrected,
        text,
        url,
        pushSearchResultsEvent,
        searchCategoryId,
        searchCategoryName,
        removeAdSpacerFromItems,
        setInitialScrollCompleted,
        setFirstElementId,
        injectedAdHiddenForTimeout,
        injectedAdsEnabled,
        selectedSort,
        filterSelectedCount,
        hideFooterUntilLastPage,
        initialScrollCompleted,
        productLoadTrigger,
        im,
        productSummaryTemplateName,
        imageRatio,
        enableNewProductSummary,
        isAdsEnabled,
        urlIsSaleOrClearance,
        saleOrClearance,
        advertsContent,
        adsConfig,
    } = props

    const totalItems = items.length
    const gridContainerRef = useRef<HTMLDivElement>(null)
    const firstElementRef = useRef<HTMLDivElement>(null)
    const gridContainerElement = gridContainerRef.current

    useEffect(() => {
        if (!injectedAdsEnabled) return
        let iteration = 0
        const interval = setInterval(() => {
            iteration += 1
            if (adRenderedRef.current === true) {
                clearInterval(interval)
            }
            if (iteration === 8) {
                clearInterval(interval)
                removeAdSpacerFromItems()
            }
        }, 500)
        return () => {
            if (!injectedAdsEnabled) return
            adRenderedRef.current = false
            clearInterval(interval)
        }
    }, [removeAdSpacerFromItems])

    useEffect(() => {
        setShouldRemoveInjectedAdSpacer(!injectedAdHiddenForTimeout)
    }, [injectedAdHiddenForTimeout, injectedAdsEnabled])

    useInfiniteScroll({
        endPage,
        startPage,
        totalItems,
        hasNextPage,
        itemsPerPage,
        hasPreviousPage,
        fetchNextPage,
        fetchPreviousPage,
        fetchTriggerOffset,
        gridContainerElement,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isFetchingPageItems,
        onBeforeFetchingPreviousPage: fixFacets,
        onAfterRestoringUpScrollPosition: unfixFacets,
        initialScrollCompleted,
        productLoadTrigger,
    })

    useInitialScrollPosition({
        requestedPage,
        gridContainerElement,
        onWillRepositionScroll: fixFacets,
        onAfterScrollReposition: unfixFacets,
        fetchNextPage,
        setInitialScrollCompleted,
        productLoadTrigger,
        shouldScrollPage: false,
    })

    useScrollPositionTracking({
        startPage,
        itemsPerPage,
        gridContainerElement,
        initialScrollCompleted,
    })

    useEffect(() => {
        const event = new Event("hydrate-chunks-product-summary")
        window.dispatchEvent(event)
        publishProductNumbersToMonetate(items.map(item => item.itemNumber))
    }, [items])

    const prevItemsCount = useRef(items.length)
    const prevSortOption = useRef(selectedSort)
    const prevFilterCount = useRef(filterSelectedCount)
    const itemsHaveUpdated = useRef(false)
    useEffect(() => {
        if (
            items.length < prevItemsCount.current ||
            selectedSort !== prevSortOption.current ||
            filterSelectedCount !== prevFilterCount.current
        ) {
            prevItemsCount.current = items.length
            prevSortOption.current = selectedSort
            // Set itemsHaveUpdated to true only if google ads is enable and items change, avoiding ad display on sort/filter changes
            // TODO: Remove this while decommissioning the Google ads
            itemsHaveUpdated.current = injectedAdsEnabled || false
        }
    }, [items, selectedSort, filterSelectedCount])

    useHideFooterUntilLastPage(hideFooterUntilLastPage, hasNextPage)

    useEffect(() => {
        if (firstElementRef.current && firstElementRef.current.id) {
            setFirstElementId(firstElementRef.current.id)
        }
    }, [])

    useEffect(() => {
        const ids = items.map(({itemNumber}, index) => ({
            itemNumber,
            index: (startPage - 1) * itemsPerPage + index,
        }))
        PublishProductSummaryTrackPage(ids)
        handleBloomreachCategoryInfo({id: searchCategoryId, name: searchCategoryName})
        pushSearchResultsEvent()
    }, [])

    useEffect(() => {
        if (injectedAdsEnabled) {
            window.addEventListener(
                "message",
                function (e) {
                    if (e.origin === GPT_ADVERT_ORIGIN) {
                        const msgData = JSON.parse(e.data)
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, camelcase
                        const {creative_id, unique_id, placement_id} = msgData

                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        if (msgData.type === "load") {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                            TrackInjectedAdLoad(creative_id, unique_id, placement_id)
                        }

                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        if (msgData.type === "click") {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                            TrackInjectedAdClick(creative_id, unique_id, placement_id)
                        }
                    }
                },
                false,
            )
        }
    }, [injectedAdsEnabled])

    useEffect(() => {
        if (!injectedAdsEnabled) return
        if (!isFetchingPageItems) {
            // if isFetchingPageItems is false, then it means that the page has finished loading
            // and we can now refresh any ads that are on the page
            GPT.refresh()
        } else {
            GPT.removeAllListeners()
        }
    }, [isFetchingPageItems])

    useEffect(() => {
        if (injectedAdsEnabled) {
            GPT.on(Events.SLOT_RENDER_ENDED, e => {
                adRenderedRef.current = true
                if (e.isEmpty) {
                    removeAdSpacerFromItems()
                }
            })
            return () => {
                GPT.removeAllListeners()
            }
        }
    }, [injectedAdsEnabled])

    return (
        <>
            <SearchProviderApiCall />
            {isFetchingPreviousPage && initialScrollCompleted && (
                <ConnectPrevNextSpinner
                    ariaValueText={text.labels.loadingPreviousPage}
                    testid="plp-previous-page-spinner"
                />
            )}
            <SeoLink url={url} title={text.labels.previous} />
            <ProductGridWrapper
                className="plp-product-grid-wrapper"
                isAutocorrected={isAutocorrected}
                urlIsSaleOrClearance={urlIsSaleOrClearance}
            >
                {!isFetchingPageItems && items.length === 0 && (
                    <NoResultsContainer data-testid="plp-no-results-container">
                        {text.pages.products.noResults}
                    </NoResultsContainer>
                )}
                {isFetchingPageItems && (
                    <LoadingCover
                        ariaValueText={text.labels.loadingProducts}
                        testId="plp-results-loading-page-spinner"
                    />
                )}
                {items?.length > 0 && (
                    <ProductGrid ref={gridContainerRef} data-testid="plp-product-grid">
                        {items.map((item, index) => {
                            if (
                                (injectedAdsEnabled || isAdsEnabled) &&
                                item.itemNumber === "ad_placeholder_item" &&
                                shouldRemoveInjectedAdSpacer &&
                                itemsHaveUpdated.current === false
                            ) {
                                return (
                                    <ProductGridItem
                                        ref={index === 0 ? firstElementRef : null}
                                        key={item.itemNumber}
                                        data-testid="plp-ads-grid-item"
                                        id="plp-ads-grid-item"
                                        tileCount={item.tileCount}
                                        itemsPerRow={itemsPerRow}
                                        injectedAd
                                    >
                                        {injectedAdsEnabled ? (
                                            <InjectedAd />
                                        ) : (
                                            <Ads
                                                removeAdSpacerFromItems={removeAdSpacerFromItems}
                                                adsId={item.adsId}
                                                advertsContent={advertsContent}
                                            />
                                        )}
                                    </ProductGridItem>
                                )
                            }
                            if (item.itemNumber === "ad_placeholder_item") return null
                            return (
                                <ProductGridItem
                                    ref={index === 0 ? firstElementRef : null}
                                    key={item.itemNumber}
                                    data-testid="plp-product-grid-item"
                                    data-index={(startPage - 1) * itemsPerPage + index}
                                    id={productIdFor(item.itemNumber, item.type)}
                                    itemsPerRow={itemsPerRow}
                                    tileCount={1}
                                >
                                    {enableNewProductSummary ? (
                                        <ProductSummary {...item} />
                                    ) : (
                                        <Product
                                            itemNumber={item.itemNumber}
                                            itemType={item.type}
                                            newIn={item.newIn}
                                            html={item.html}
                                            siteUrl={siteUrl}
                                            useDevEsi={useDevEsi}
                                            im={im}
                                            productSummaryTemplateName={productSummaryTemplateName}
                                            imageRatio={imageRatio}
                                            saleOrClearance={saleOrClearance}
                                            title={item.title}
                                            isOnClient={isOnClient}
                                        />
                                    )}
                                </ProductGridItem>
                            )
                        })}
                    </ProductGrid>
                )}
            </ProductGridWrapper>
            <SeoLink url={url} hasNextPage={hasNextPage} title={text.labels.next} />
            {productLoadTrigger === LOAD_MORE && <LoadMoreArea />}
            {isFetchingNextPage && (
                <ConnectPrevNextSpinner ariaValueText={text.labels.loadingNextPage} testid="plp-next-page-spinner" />
            )}
            <BackToTop />
        </>
    )
}

export default connect(Products)
